import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  Chip,
  Button
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  FileDownload as FileDownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { RootState } from '@/store/store';
import { fetchChannelPerformanceBreakdownStart } from '@/store/slices/dashboardSlice';
import { formatNumber, formatPercentage } from '@/utils/formatters';
import { ChannelBreakdown, SubchannelItem, LOBItem, ProductItem, SubProductItem } from '@/services/api';

interface ChannelPerformanceBreakdownProps {
  compact?: boolean;
}

// Use the same color scheme as in RegionManagerDrillDown
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD',
  channelColors: {
    'Channel': { bg: '#00796B', text: '#FFFFFF', border: '#00695C' },     // Teal
    'SubChannel': { bg: '#26A69A', text: '#FFFFFF', border: '#00796B' },  // Lighter Teal
    'LOB': { bg: '#5D4037', text: '#FFFFFF', border: '#4E342E' },         // Brown
    'Product': { bg: '#7B1FA2', text: '#FFFFFF', border: '#6A1B9A' },     // Purple
    'SubProduct': { bg: '#9C27B0', text: '#FFFFFF', border: '#8E24AA' }   // Light Purple
  }
};

const getConversionColor = (rate: number, theme: any) => {
  if (rate >= 70) return '#4CAF50'; // Green for 70% and above
  if (rate >= 40) return '#FF6E00'; // Orange for 40-69%
  return '#F44336'; // Red for less than 40%
};

interface ChannelRowProps {
  channel: ChannelBreakdown;
  columnWidths: {
    name: string;
    target: string;
    ftmBase: string;
    ftmConv: string;
    ftmRate: string;
    aodBase: string;
    aodConv: string;
    aodRate: string;
  };
}

const ChannelRow = ({ channel, columnWidths }: ChannelRowProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = channel.sub_channels && channel.sub_channels.length > 0;
  
  // Use the channel-specific colors
  const channelColor = CHOLA_COLORS.channelColors['Channel'] || { 
    bg: theme.palette.primary.light, 
    text: theme.palette.primary.dark, 
    border: theme.palette.primary.main 
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: `${channelColor.bg}08` },
          cursor: hasChildren ? 'pointer' : 'default',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <TableCell 
          sx={{ 
            pl: 2,
            width: '250px',
            minWidth: '250px',
            maxWidth: '250px',
            borderRight: '1px solid rgba(224, 224, 224, 1)'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            width: '100%',
            overflow: 'hidden'
          }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: channelColor.border,
                  p: 0.25,
                  flexShrink: 0,
                  mr: 1
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 24, flexShrink: 0, mr: 1 }} />
            )}
            
            <Box sx={{ 
              flexGrow: 1,
              maxWidth: 'calc(100% - 70px)', // Leave space for the channel chip
              overflow: 'hidden'
            }}>
              <Typography 
                variant="body2" 
                fontWeight={500}
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  display: 'block'
                }}
              >
                {channel.channel}
              </Typography>
            </Box>
            
            <Box sx={{ 
              ml: 1, 
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Chip
                label="Channel"
                size="small"
                sx={{
                  bgcolor: channelColor.bg,
                  color: channelColor.text,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: '20px',
                  borderRadius: '4px',
                  maxWidth: '70px'
                }}
              />
            </Box>
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: 600,
              color: channel.target ? CHOLA_COLORS.primary : 'text.secondary'
            }}
          >
            {channel.target ? `${channel.target}%` : '-'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(channel.ftm.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(channel.ftm.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(channel.ftm.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(channel.ftm.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(channel.aod.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(channel.aod.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(channel.aod.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(channel.aod.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>
      </TableRow>

      {expanded && hasChildren && (
        <TableRow>
          <TableCell 
            colSpan={8} 
            sx={{ 
              p: 0, 
              border: 'none',
              bgcolor: `${channelColor.bg}04`,
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 0 }}>
                <Table size="small" sx={{ 
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.75,
                    borderColor: theme.palette.divider,
                  },
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  }
                }}>
                  <colgroup>
                    <col style={{ width: columnWidths.name }} />
                    <col style={{ width: columnWidths.target }} />
                    <col style={{ width: columnWidths.ftmBase }} />
                    <col style={{ width: columnWidths.ftmConv }} />
                    <col style={{ width: columnWidths.ftmRate }} />
                    <col style={{ width: columnWidths.aodBase }} />
                    <col style={{ width: columnWidths.aodConv }} />
                    <col style={{ width: columnWidths.aodRate }} />
                  </colgroup>
                  <TableBody>
                    {channel.sub_channels.map((subchannel) => (
                      <SubchannelRow
                        key={subchannel.subchannel}
                        subchannel={subchannel}
                        columnWidths={columnWidths}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface SubchannelRowProps {
  subchannel: SubchannelItem;
  columnWidths: {
    name: string;
    target: string;
    ftmBase: string;
    ftmConv: string;
    ftmRate: string;
    aodBase: string;
    aodConv: string;
    aodRate: string;
  };
}

const SubchannelRow = ({ subchannel, columnWidths }: SubchannelRowProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = subchannel.lobs && subchannel.lobs.length > 0;
  
  // Use the subchannel-specific colors
  const subchannelColor = CHOLA_COLORS.channelColors['SubChannel'] || { 
    bg: theme.palette.secondary.light, 
    text: theme.palette.secondary.dark, 
    border: theme.palette.secondary.main 
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: `${subchannelColor.bg}08` },
          cursor: hasChildren ? 'pointer' : 'default'
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <TableCell>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            width: '100%',
            overflow: 'hidden'
          }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: subchannelColor.border,
                  p: 0.25,
                  flexShrink: 0,
                  mr: 1
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 24, flexShrink: 0, mr: 1 }} />
            )}
            
            <Box sx={{ 
              flexGrow: 1,
              maxWidth: 'calc(100% - 95px)',
              overflow: 'hidden'
            }}>
              <Typography 
                variant="body2" 
                fontWeight={500}
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  display: 'block'
                }}
              >
                {subchannel.subchannel}
              </Typography>
            </Box>
            
            <Box sx={{ 
              ml: 1, 
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Chip
                label="SubChannel"
                size="small"
                sx={{
                  bgcolor: subchannelColor.bg,
                  color: subchannelColor.text,
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  height: '18px',
                  borderRadius: '4px',
                  maxWidth: '90px'
                }}
              />
            </Box>
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: 600,
              color: subchannel.target ? CHOLA_COLORS.primary : 'text.secondary'
            }}
          >
            {subchannel.target ? `${subchannel.target}%` : '-'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(subchannel.ftm.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(subchannel.ftm.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(subchannel.ftm.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(subchannel.ftm.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(subchannel.aod.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(subchannel.aod.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(subchannel.aod.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(subchannel.aod.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>
      </TableRow>

      {expanded && hasChildren && (
        <TableRow>
          <TableCell 
            colSpan={8} 
            sx={{ 
              p: 0, 
              border: 'none', 
              bgcolor: `${subchannelColor.bg}04`,
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 0 }}>
                <Table size="small" sx={{ 
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.75,
                    borderColor: theme.palette.divider,
                  },
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  }
                }}>
                  <colgroup>
                    <col style={{ width: columnWidths.name }} />
                    <col style={{ width: columnWidths.target }} />
                    <col style={{ width: columnWidths.ftmBase }} />
                    <col style={{ width: columnWidths.ftmConv }} />
                    <col style={{ width: columnWidths.ftmRate }} />
                    <col style={{ width: columnWidths.aodBase }} />
                    <col style={{ width: columnWidths.aodConv }} />
                    <col style={{ width: columnWidths.aodRate }} />
                  </colgroup>
                  <TableBody>
                    {subchannel.lobs && subchannel.lobs.map((lob) => (
                      <LobRow 
                        key={lob.lob} 
                        lob={lob}
                        columnWidths={columnWidths}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface LobRowProps {
  lob: LOBItem;
  columnWidths: {
    name: string;
    target: string;
    ftmBase: string;
    ftmConv: string;
    ftmRate: string;
    aodBase: string;
    aodConv: string;
    aodRate: string;
  };
}

const LobRow = ({ lob, columnWidths }: LobRowProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = lob.products && lob.products.length > 0;
  
  // Use the LOB-specific colors
  const lobColor = CHOLA_COLORS.channelColors['LOB'] || { 
    bg: theme.palette.success.light, 
    text: theme.palette.success.dark, 
    border: theme.palette.success.main 
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: `${lobColor.bg}08` },
          cursor: hasChildren ? 'pointer' : 'default'
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: lobColor.border,
                  p: 0.25
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 18 }} />
            )}
            <Typography 
              variant="body2" 
              fontWeight={500}
              sx={{ color: 'text.primary' }}
            >
              {lob.lob}
            </Typography>
            <Chip
              label="LOB"
              size="small"
              sx={{
                bgcolor: lobColor.bg,
                color: lobColor.text,
                fontWeight: 500,
                fontSize: '0.65rem',
                height: '18px',
                borderRadius: '4px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: 600,
              color: lob.target ? CHOLA_COLORS.primary : 'text.secondary'
            }}
          >
            {lob.target ? `${lob.target}%` : '-'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(lob.ftm.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(lob.ftm.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(lob.ftm.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(lob.ftm.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(lob.aod.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(lob.aod.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(lob.aod.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(lob.aod.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>
      </TableRow>

      {expanded && hasChildren && (
        <TableRow>
          <TableCell 
            colSpan={8} 
            sx={{ 
              p: 0, 
              border: 'none', 
              bgcolor: `${lobColor.bg}04`,
              position: 'relative',
              '&::before': {
                content: 'none'
              }
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 0 }}>
                <Table size="small" sx={{ 
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.75,
                    borderColor: theme.palette.divider,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  },
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    borderLeft: 'none',
                  },
                  '& .MuiTableCell-root:last-of-type': {
                    borderRight: 'none',
                  }
                }}>
                  <colgroup>
                    <col style={{ width: columnWidths.name }} />
                    <col style={{ width: columnWidths.target }} />
                    <col style={{ width: columnWidths.ftmBase }} />
                    <col style={{ width: columnWidths.ftmConv }} />
                    <col style={{ width: columnWidths.ftmRate }} />
                    <col style={{ width: columnWidths.aodBase }} />
                    <col style={{ width: columnWidths.aodConv }} />
                    <col style={{ width: columnWidths.aodRate }} />
                  </colgroup>
                  <TableBody>
                    {lob.products && lob.products.map((product) => (
                      <ProductRow 
                        key={product.product} 
                        product={product}
                        columnWidths={columnWidths}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface ProductRowProps {
  product: ProductItem;
  columnWidths: {
    name: string;
    target: string;
    ftmBase: string;
    ftmConv: string;
    ftmRate: string;
    aodBase: string;
    aodConv: string;
    aodRate: string;
  };
}

const ProductRow = ({ product, columnWidths }: ProductRowProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = product.sub_products && product.sub_products.length > 0;
  
  // Use the Product-specific colors
  const productColor = CHOLA_COLORS.channelColors['Product'] || { 
    bg: theme.palette.info.light, 
    text: theme.palette.info.dark, 
    border: theme.palette.info.main 
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { bgcolor: `${productColor.bg}08` },
          cursor: hasChildren ? 'pointer' : 'default'
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: productColor.border,
                  p: 0.25
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 18 }} />
            )}
            <Typography 
              variant="body2" 
              fontWeight={500}
              sx={{ color: 'text.primary' }}
            >
              {product.product}
            </Typography>
            <Chip
              label="Product"
              size="small"
              sx={{
                bgcolor: productColor.bg,
                color: productColor.text,
                fontWeight: 500,
                fontSize: '0.65rem',
                height: '18px',
                borderRadius: '4px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: 600,
              color: product.target ? CHOLA_COLORS.primary : 'text.secondary'
            }}
          >
            {product.target ? `${product.target}%` : '-'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(product.ftm.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(product.ftm.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(product.ftm.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(product.ftm.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(product.aod.total_leads).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2">
            {parseInt(product.aod.converted).toLocaleString()}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: getConversionColor(parseFloat(product.aod.conversion_rate), theme),
              fontWeight: 600
            }}
          >
            {parseFloat(product.aod.conversion_rate).toFixed(1)}%
          </Typography>
        </TableCell>
      </TableRow>

      {expanded && hasChildren && (
        <TableRow>
          <TableCell 
            colSpan={8} 
            sx={{ 
              p: 0, 
              border: 'none', 
              bgcolor: `${productColor.bg}04`,
              position: 'relative',
              '&::before': {
                content: 'none'
              }
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 0 }}>
                <Table size="small" sx={{ 
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.75,
                    borderColor: theme.palette.divider,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  },
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    borderLeft: 'none',
                  },
                  '& .MuiTableCell-root:last-of-type': {
                    borderRight: 'none',
                  }
                }}>
                  <colgroup>
                    <col style={{ width: columnWidths.name }} />
                    <col style={{ width: columnWidths.target }} />
                    <col style={{ width: columnWidths.ftmBase }} />
                    <col style={{ width: columnWidths.ftmConv }} />
                    <col style={{ width: columnWidths.ftmRate }} />
                    <col style={{ width: columnWidths.aodBase }} />
                    <col style={{ width: columnWidths.aodConv }} />
                    <col style={{ width: columnWidths.aodRate }} />
                  </colgroup>
                  <TableBody>
                    {product.sub_products && product.sub_products.map((subProduct) => (
                      <SubProductRow 
                        key={subProduct.sub_product} 
                        subProduct={subProduct}
                        columnWidths={columnWidths}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface SubProductRowProps {
  subProduct: SubProductItem;
  columnWidths: {
    name: string;
    target: string;
    ftmBase: string;
    ftmConv: string;
    ftmRate: string;
    aodBase: string;
    aodConv: string;
    aodRate: string;
  };
}

const SubProductRow = ({ subProduct, columnWidths }: SubProductRowProps) => {
  const theme = useTheme();
  
  // Use the SubProduct-specific colors
  const subProductColor = CHOLA_COLORS.channelColors['SubProduct'] || { 
    bg: theme.palette.error.light, 
    text: theme.palette.error.dark, 
    border: theme.palette.error.main 
  };

  return (
    <TableRow 
      sx={{ 
        '&:hover': { bgcolor: `${subProductColor.bg}08` }
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 18 }} />
          <Typography 
            variant="body2" 
            fontWeight={500}
            sx={{ color: 'text.primary' }}
          >
            {subProduct.sub_product}
          </Typography>
          <Chip
            label="SubProduct"
            size="small"
            sx={{
              bgcolor: subProductColor.bg,
              color: subProductColor.text,
              fontWeight: 500,
              fontSize: '0.65rem',
              height: '18px',
              borderRadius: '4px',
              '& .MuiChip-label': {
                px: 1,
                py: 0
              }
            }}
          />
        </Box>
      </TableCell>

      <TableCell align="center">
        <Typography 
          variant="body2"
          sx={{ 
            fontWeight: 600,
            color: subProduct.target ? CHOLA_COLORS.primary : 'text.secondary'
          }}
        >
          {subProduct.target ? `${subProduct.target}%` : '-'}
        </Typography>
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2">
          {parseInt(subProduct.ftm.total_leads).toLocaleString()}
        </Typography>
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2">
          {parseInt(subProduct.ftm.converted).toLocaleString()}
        </Typography>
      </TableCell>

      <TableCell align="center">
        <Typography 
          variant="body2" 
          sx={{ 
            color: getConversionColor(parseFloat(subProduct.ftm.conversion_rate), theme),
            fontWeight: 600
          }}
        >
          {parseFloat(subProduct.ftm.conversion_rate).toFixed(1)}%
        </Typography>
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2">
          {parseInt(subProduct.aod.total_leads).toLocaleString()}
        </Typography>
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2">
          {parseInt(subProduct.aod.converted).toLocaleString()}
        </Typography>
      </TableCell>

      <TableCell align="center">
        <Typography 
          variant="body2" 
          sx={{ 
            color: getConversionColor(parseFloat(subProduct.aod.conversion_rate), theme),
            fontWeight: 600
          }}
        >
          {parseFloat(subProduct.aod.conversion_rate).toFixed(1)}%
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const ChannelPerformanceBreakdown: React.FC<ChannelPerformanceBreakdownProps> = ({ compact = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { 
    channelPerformanceBreakdown, 
    channelPerformanceBreakdownLoading, 
    channelPerformanceBreakdownError 
  } = useSelector((state: RootState) => state.dashboard);

  // Define fixed column widths to be used consistently across all tables
  const columnWidths = {
    name: '250px',
    target: '100px',
    ftmBase: '100px',
    ftmConv: '100px',
    ftmRate: '100px',
    aodBase: '100px',
    aodConv: '100px',
    aodRate: '100px'
  };

  // Calculate totals for all channels
  const calculateTotals = () => {
    if (!channelPerformanceBreakdown) return {
      ftm: { total_base: 0, conversion: 0, conv_rate: 0 },
      aod: { total_base: 0, conversion: 0, conv_rate: 0 }
    };

    let ftmTotalBase = 0;
    let ftmConversion = 0;
    let aodTotalBase = 0;
    let aodConversion = 0;

    channelPerformanceBreakdown.channels.forEach(channel => {
      ftmTotalBase += parseInt(channel.ftm.total_leads);
      ftmConversion += parseInt(channel.ftm.converted);
      aodTotalBase += parseInt(channel.aod.total_leads);
      aodConversion += parseInt(channel.aod.converted);
    });

    const ftmConvRate = ftmTotalBase > 0 ? (ftmConversion / ftmTotalBase * 100) : 0;
    const aodConvRate = aodTotalBase > 0 ? (aodConversion / aodTotalBase * 100) : 0;

    return {
      ftm: {
        total_base: ftmTotalBase,
        conversion: ftmConversion,
        conv_rate: ftmConvRate
      },
      aod: {
        total_base: aodTotalBase,
        conversion: aodConversion,
        conv_rate: aodConvRate
      }
    };
  };

  const totalMetrics = calculateTotals();

  const handleExport = () => {
    if (!channelPerformanceBreakdown) return;

    // Helper function to create CSV data for any level
    const createCSV = (
      data: any,
      level: 'Channel' | 'SubChannel' | 'LOB' | 'Product' | 'SubProduct',
      parentName: string = ''
    ) => {
      let rows: string[] = [];
      
      // Headers
      if (rows.length === 0) {
        rows.push(`Level,Name,Parent,Target,FTM Total,FTM Converted,FTM Conv Rate,AOD Total,AOD Converted,AOD Conv Rate`);
      }
      
      // Get the name property based on level
      const getName = (item: any): string => {
        switch (level) {
          case 'Channel':
            return item.channel;
          case 'SubChannel':
            return item.subchannel;
          case 'LOB':
            return item.lob;
          case 'Product':
            return item.product;
          case 'SubProduct':
            return item.sub_product;
          default:
            return '';
        }
      };
      
      // Add data for this item
      data.forEach((item: any) => {
        const name = getName(item);
        const target = item.target ? item.target : '';
        
        // Add row for current level
        rows.push(
          `${level},${name},${parentName},${target},` +
          `${parseInt(item.ftm.total_leads).toLocaleString()},` +
          `${parseInt(item.ftm.converted).toLocaleString()},` +
          `${parseFloat(item.ftm.conversion_rate).toFixed(1)}%,` +
          `${parseInt(item.aod.total_leads).toLocaleString()},` +
          `${parseInt(item.aod.converted).toLocaleString()},` +
          `${parseFloat(item.aod.conversion_rate).toFixed(1)}%`
        );
        
        // Recursively add children
        if (level === 'Channel' && item.sub_channels) {
          item.sub_channels.forEach((subChannel: any) => {
            rows = rows.concat(createCSV([subChannel], 'SubChannel', name));
            
            if (subChannel.lobs) {
              subChannel.lobs.forEach((lob: any) => {
                rows = rows.concat(createCSV([lob], 'LOB', subChannel.subchannel));
                
                if (lob.products) {
                  lob.products.forEach((product: any) => {
                    rows = rows.concat(createCSV([product], 'Product', lob.lob));
                    
                    if (product.sub_products) {
                      rows = rows.concat(createCSV(product.sub_products, 'SubProduct', product.product));
                    }
                  });
                }
              });
            }
          });
        }
      });
      
      return rows;
    };
    
    // Create CSV content
    const csvRows = createCSV(channelPerformanceBreakdown.channels, 'Channel');
    const csvContent = csvRows.join('\n');
    
    // Download the CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `channel_performance_breakdown.csv`;
    link.click();
  };

  if (channelPerformanceBreakdownLoading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (channelPerformanceBreakdownError) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography color="error">{channelPerformanceBreakdownError}</Typography>
          <Button variant="contained" onClick={() => dispatch(fetchChannelPerformanceBreakdownStart())}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!channelPerformanceBreakdown) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography>No channel data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ 
        p: compact ? 1 : 2,
        '&:last-child': { pb: compact ? 1 : 2 }
      }}>
        {/* Hidden button for external triggering of export */}
        <IconButton
          id="channel-performance-export-btn"
          onClick={handleExport}
          sx={{ display: 'none' }}
        >
          <FileDownloadIcon />
        </IconButton>
        <Box sx={{ height: 'auto', width: '100%', overflow: 'auto' }}>
          <TableContainer sx={{ 
            maxHeight: compact ? 300 : 400, 
            overflow: 'auto',
            minWidth: '100%',
            '& table': {
              width: 'auto',
              minWidth: '100%'
            }
          }}>
            <Table sx={{ 
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                px: 1,
                py: 1,
                borderColor: theme.palette.divider,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                borderTop: 'none',
              },
              '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTableCell-root:first-of-type': {
                borderLeft: 'none',
              },
              '& .MuiTableCell-root:last-of-type': {
                borderRight: 'none',
              }
            }} size={compact ? "small" : "medium"} stickyHeader>
              <colgroup>
                <col style={{ width: '250px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Channel
                  </TableCell>
                  <TableCell 
                    rowSpan={2}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                    align="center"
                  >
                    Target
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    FTM
                  </TableCell>
                  <TableCell 
                    align="center"
                    colSpan={3}
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    AOD
                  </TableCell>
                </TableRow>
                <TableRow>
                  {/* Total Base, Converted, Conv. Rate subheaders for FTM */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                  
                  {/* Total Base, Converted, Conv. Rate subheaders for AOD */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Total Base
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Converted
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      fontWeight: 'medium',
                      fontSize: '0.8125rem',
                      py: 1,
                      position: 'sticky',
                      top: 30,
                      zIndex: 2,
                      borderBottom: `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Conv. Rate
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channelPerformanceBreakdown.channels.map((channel) => (
                  <ChannelRow 
                    key={channel.channel} 
                    channel={channel}
                    columnWidths={columnWidths}
                  />
                ))}
                
                {/* Total Row for all Channels */}
                {channelPerformanceBreakdown.channels.length > 0 && (
                  <TableRow 
                    sx={{ 
                      bgcolor: theme.palette.grey[50],
                      borderTop: `2px solid ${theme.palette.divider}`,
                      '& .MuiTableCell-root': {
                        fontWeight: 'bold',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: theme.palette.grey[50],
                        zIndex: 1,
                        py: 1.5,
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        TOTAL (All Channels)
                      </Typography>
                    </TableCell>
                    
                    {/* Target column - but empty for total row */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        -
                      </Typography>
                    </TableCell>
                    
                    {/* FTM Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.ftm.total_base.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.ftm.conversion.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: getConversionColor(totalMetrics.ftm.conv_rate, theme)
                        }}
                      >
                        {totalMetrics.ftm.conv_rate.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    
                    {/* AOD Metrics */}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.aod.total_base.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {totalMetrics.aod.conversion.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          color: getConversionColor(totalMetrics.aod.conv_rate, theme)
                        }}
                      >
                        {totalMetrics.aod.conv_rate.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
};
